import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import {
  type BannerClassStudentDropDownViewModel,
  getApiV1BannerClassStudentsQueryOptions,
} from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

import type { BannerClassStudentFormValue } from '../types/bannerForm';

/** Turmas por requisição na varredura de uma marca. */
const ALLOCATION_PAGE_SIZE = 200;

/** Teto de páginas por marca — guarda contra uma paginação que não termina. */
const MAX_PAGES = 25;

/** Converte a turma do lookup no valor do formulário, marcada com a marca de origem. */
function toFormValue(
  item: BannerClassStudentDropDownViewModel,
  brandId: number,
): BannerClassStudentFormValue | null {
  if (item.id === undefined) return null;

  return {
    classStudentId: item.id,
    description: item.code ? `${item.name ?? ''} · ${item.code}`.trim() : (item.name ?? ''),
    brandId,
    ...(item.brandName ? { brandName: item.brandName } : {}),
  };
}

/**
 * Busca **todas** as turmas de uma marca, para o filtro de marca vincular em bloco.
 *
 * O filtro de marca não estreita a busca do autocomplete: marcar uma marca vincula
 * ao banner todas as turmas dela. Como o lookup é paginado, este hook varre as
 * páginas (via `queryClient.fetchQuery`, aproveitando o cache do TanStack) e
 * devolve a lista inteira já no formato do formulário, com `brandId` gravado — é
 * ele que permite desfazer a alocação ao desmarcar a marca.
 *
 * @returns `{ fetchByBrand, isAllocating }`.
 *
 * @example
 * ```ts
 * const { fetchByBrand } = useBannerBrandAllocation();
 * const turmas = await fetchByBrand(7, true);
 * ```
 */
export function useBannerBrandAllocation() {
  const queryClient = useQueryClient();
  const [isAllocating, setIsAllocating] = useState(false);

  const fetchByBrand = useCallback(
    async (brandId: number, activeOnly: boolean): Promise<BannerClassStudentFormValue[]> => {
      setIsAllocating(true);
      try {
        const collected: BannerClassStudentFormValue[] = [];

        for (let page = 1; page <= MAX_PAGES; page += 1) {
          const response = await queryClient.fetchQuery(
            getApiV1BannerClassStudentsQueryOptions({
              BrandIds: [brandId],
              ActiveOnly: activeOnly,
              Page: page,
              PageSize: ALLOCATION_PAGE_SIZE,
            }),
          );

          const items = unwrapPagedResponse<BannerClassStudentDropDownViewModel>(response);
          for (const item of items) {
            const value = toFormValue(item, brandId);
            if (value) collected.push(value);
          }

          if (items.length < ALLOCATION_PAGE_SIZE) break;
        }

        return collected;
      } finally {
        setIsAllocating(false);
      }
    },
    [queryClient],
  );

  return { fetchByBrand, isAllocating } as const;
}
